import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { billingClient } from 'api/billing';
import type { BillingProfileResponse, BillingProfileUnavailableField } from 'api/types';
import { QueryKeys } from 'entities/billing/api/common';
import { createUnavailableTracker, UNAVAILABLE_POLL_INTERVAL_MS } from 'entities/billing/api/unavailablePolling';

const POLL_INTERVAL_MS = 5000;

const NO_UNAVAILABLE_FIELDS: BillingProfileUnavailableField[] = [];

const unavailableTracker = createUnavailableTracker();

const isTemporaryState = (data: BillingProfileResponse | undefined) => {
  if (!data) {
    return false;
  }

  return (
    data.subscription_status === 'in_progress' ||
    data.payment_status === 'processing' ||
    data.has_pending_credits === true
  );
};

const hasUnavailableFields = (data: BillingProfileResponse | undefined) => Boolean(data?.unavailable_fields?.length);

type UseGetBillingProfileOptions = {
  enabled?: boolean;
};

export const useGetBillingProfile = ({ enabled = true }: UseGetBillingProfileOptions = {}) => {
  const query = useQuery({
    queryKey: [QueryKeys.getBillingProfile],
    queryFn: () => billingClient.getBillingProfile(),
    enabled,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 1000 * 30,
    refetchInterval: (query) => {
      const data = query.state.data;

      unavailableTracker.track(hasUnavailableFields(data));

      if (isTemporaryState(data)) {
        return POLL_INTERVAL_MS;
      }

      if (hasUnavailableFields(data) && !unavailableTracker.isGiveUpReached()) {
        return UNAVAILABLE_POLL_INTERVAL_MS;
      }

      return false;
    },
  });

  const { refetch } = query;
  const retryUnavailableFields = useCallback(() => {
    unavailableTracker.reset();
    void refetch();
  }, [refetch]);

  return {
    ...query,
    unavailableFields: query.data?.unavailable_fields ?? NO_UNAVAILABLE_FIELDS,
    isUnavailablePollingExhausted: hasUnavailableFields(query.data) && unavailableTracker.isGiveUpReached(),
    retryUnavailableFields,
  };
};
